import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBookingStatus } from '@/hooks/use-booking-status';
import { Loader2, RefreshCw, Clock, CheckCircle, AlertCircle, Calendar, CreditCard } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const statusConfig = {
  pending_schedule: { 
    label: 'Pending Schedule', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Calendar,
    description: 'Waiting for appointment scheduling'
  },
  pending_payment: { 
    label: 'Pending Payment', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: CreditCard,
    description: 'Payment processing or required'
  },
  scheduled: { 
    label: 'Scheduled', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Clock,
    description: 'Appointment confirmed and scheduled'
  },
  in_progress: { 
    label: 'In Progress', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: RefreshCw,
    description: 'Service currently being performed'
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    description: 'Service completed successfully'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
    description: 'Booking has been cancelled'
  }
};

const BookingStatusTracker: React.FC = () => {
  const { bookings, isLoading, error, refreshBookings } = useBookingStatus();

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading booking status...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardContent className="py-6">
          <div className="flex items-center gap-2 text-red-600 mb-4">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error loading bookings</span>
          </div>
          <p className="text-sm text-red-700 mb-4">{error}</p>
          <Button onClick={refreshBookings} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (bookings.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="text-center py-8">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-medium text-lg mb-2">No Bookings Yet</h3>
          <p className="text-muted-foreground">
            Your booking history will appear here once you make your first reservation.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Recent Bookings</h2>
        <Button onClick={refreshBookings} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4">
        {bookings.map((booking) => {
          const config = statusConfig[booking.status];
          const StatusIcon = config.icon;
          
          return (
            <Card key={booking.id} className="w-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{booking.service_type}</CardTitle>
                  <Badge className={`${config.color} border`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Service Size:</span>
                    <p className="font-medium">{booking.plant_count}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Price:</span>
                    <p className="font-medium">${booking.monthly_price}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">
                      {formatDistanceToNow(new Date(booking.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <StatusIcon className="h-4 w-4" />
                    <span>{config.description}</span>
                  </div>
                  {booking.address && (
                    <p className="text-sm text-muted-foreground mt-1">
                      📍 {booking.address}
                    </p>
                  )}
                </div>

                {booking.stripe_session_id && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Payment ID: {booking.stripe_session_id.substring(0, 20)}...
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default BookingStatusTracker;